import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setAuthToken, clearAuthToken } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [bootstrapping, setBootstrapping] = useState(true);
  const [loading, setLoading] = useState(false);

  // On mount: try to hydrate the session via the httpOnly cookie.
  // (Safari may not accept cookies here; that's fine — user just re-logs in.)
  // Skip the bootstrap on public routes to avoid noisy 401s in console:
  // - /share/*   → public read-only shared page
  // - /          → marketing landing (has its own LOG IN CTA)
  useEffect(() => {
    if (typeof window !== "undefined") {
      const path = window.location.pathname;
      const isPublic = path === "/" || path.startsWith("/share/");
      if (isPublic) {
        setBootstrapping(false);
        return;
      }
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        if (!cancelled) setUser(data);
      } catch {
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setBootstrapping(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const login = useCallback(async (email, password) => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      // Store the token in memory as a Bearer fallback for Safari.
      setAuthToken(data.token);
      setUser(data.user);
      return data.user;
    } finally {
      setLoading(false);
    }
  }, []);

  const register = useCallback(async (email, password, name, extras = {}) => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", { email, password, name, ...extras });
      setAuthToken(data.token);
      setUser(data.user);
      return data.user;
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    clearAuthToken();
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, bootstrapping, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
